import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { getReviewInvitation, submitVerifiedReview } from "@/lib/verified-reviews.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/review/$token")({
  head: () => ({ meta: [
    { title: "Tu experiencia — Reseña verificada | Mostrando México" },
    { name: "description", content: "Comparte tu experiencia después de viajar con Mostrando México mediante tu enlace de reserva verificada." },
    { property: "og:title", content: "Reseña verificada — Mostrando México" },
    { property: "og:description", content: "Comparte tu experiencia con Mostrando México." },
    { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex,nofollow" }, { name: "referrer", content: "no-referrer" },
  ] }),
  loader: ({ params }) => getReviewInvitation({ data: { token: params.token } }),
  component: ReviewPage,
});
function ReviewPage() {
  const invitation = Route.useLoaderData();
  const { token } = Route.useParams();
  const { i18n } = useTranslation();
  const en = i18n.language.startsWith("en");
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const submit = useServerFn(submitVerifiedReview);
  return <div className="min-h-screen bg-background"><SiteHeader /><div className="mx-auto max-w-xl px-4 py-10">
    <h1 className="font-display text-3xl">{en ? "Your travel experience" : "Tu experiencia de viaje"}</h1>
    {!invitation.valid ? <p role="status" className="mt-5">{en ? "This review link is invalid, expired or already used." : "Este enlace no es válido, venció o ya se utilizó."}</p>
      : saved ? <p role="status" className="mt-5">{en ? "Thank you! Your review is awaiting moderation." : "¡Gracias! Tu reseña está pendiente de moderación."}</p>
      : <form className="mt-6 space-y-5" onSubmit={async e => {
        e.preventDefault(); if (busy) return; setBusy(true); setError("");
        try { await submit({ data: { token, rating, title, body, country, language: en ? "en" : "es" } }); setSaved(true); }
        catch { setError(en ? "We could not save your review. Check whether the link is still valid." : "No pudimos guardar tu reseña. Comprueba que el enlace siga vigente."); }
        finally { setBusy(false); }
      }}>
        <p className="font-semibold">{invitation.title}</p><p className="text-sm text-muted-foreground">{invitation.travelDate}</p>
        <fieldset><legend className="text-sm font-medium">{en ? "Rating" : "Calificación"}</legend><div className="mt-2 flex gap-2">{[1,2,3,4,5].map(n => <Button key={n} type="button" size="icon" variant="ghost" className="min-h-11 min-w-11" aria-label={`${n} ${en ? "stars" : "estrellas"}`} aria-pressed={rating===n} onClick={() => setRating(n)}><Star className={n<=rating ? "fill-gold text-gold" : "text-muted-foreground"} /></Button>)}</div></fieldset>
        <div className="space-y-2"><Label htmlFor="review-title">{en ? "Title" : "Título"}</Label><Input id="review-title" required minLength={2} maxLength={120} value={title} onChange={e=>setTitle(e.target.value)} /></div>
        <div className="space-y-2"><Label htmlFor="review-body">{en ? "Your experience" : "Tu experiencia"}</Label><Textarea id="review-body" required minLength={10} maxLength={2000} rows={6} value={body} onChange={e=>setBody(e.target.value)} /></div>
        <div className="space-y-2"><Label htmlFor="review-country">{en ? "Country" : "País"}</Label><Input id="review-country" required minLength={2} maxLength={80} value={country} onChange={e=>setCountry(e.target.value)} /></div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={busy}>{en ? "Submit review" : "Enviar reseña"}</Button>
      </form>}
  </div></div>;
}